import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Button, Col, Row, Spinner } from 'reactstrap';
import { Table, Column, AutoSizer } from 'react-virtualized';
import Title from '../../../sharedComponents/Title/Title';
import useDataSuite from '../useDataSuite';
import useDataSuiteWidgets from './useDataSuiteWidgets';
import { FaTrash, FaClipboardList } from 'react-icons/fa';
import { BsPencilSquare } from 'react-icons/bs';
import { IoMdInformationCircle } from "react-icons/io";
import { Tooltip } from "@mui/material";
import { Theme, tableColors } from '../../../config/constants';
import { useNavigate } from 'react-router-dom';
import formatters from '../../../config/formatters';
import { isEqual } from "lodash";
import EmptySearchResultsMessage from '../../../sharedComponents/ErrorMessages/EmptySearchResultsMessage';
import EmptyDataMessage from '../../../sharedComponents/ErrorMessages/EmptyDataMessage';
import { Chart as ChartJS, registerables } from "chart.js";

ChartJS.register(...registerables);

const SegmentOverviewWidget = ({ w, i }) => {
    const navigate = useNavigate();
    const { widgetTitle, filtersEnabled } =
        useDataSuiteWidgets.useSegmentOverviewWidget();

    const { onClickWidgetDrawerOpen, onClickDeleteWidget } = useDataSuite.useWidgetControls();
    const { getSegmentOverview } = useDataSuite.useDataSuite();

    const segmentOverviewData = useSelector(({ datasuite }) => datasuite.widgetData.segmentOverviewData);
    const widgetOptionsOpen = useSelector(({ ui }) => ui.slideDrawer);
    const selectedFilters = useSelector(({ datasuite }) => datasuite.selectedFilters);
    const selectedDetailsFilters = useSelector(({ datasuite }) => datasuite.selectedDetailsFilters);
    const dateString = useSelector(({ dates }) => dates.datasuiteDateString);
    const detailsDateString = useSelector(({ dates }) => dates.datasuiteDetailsDateString);
    const isLoading = useSelector(({ datasuite }) => datasuite.isLoading.segmentOverviewLoading);

    const { numberFormatter, percentFormatter } = formatters();

    const [isWidgetHovered, setIsWidgetHovered] = useState(false);
    const prevFilters = useRef(null);
    const prevDate = useRef(null);

    const formatSegmentName = (value) => {
        if (!value) return "";
        return value.toLowerCase().replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
    };

    const onClickSeeDetails = () => {
        navigate({ pathname: '/salesstream/details', search: "?Segment%20Overview" });
    };

    useEffect(() => {
        if (isEqual(selectedFilters, prevFilters.current) &&
            isEqual(dateString, prevDate.current)) {
            getSegmentOverview(true);
        } else {
            getSegmentOverview(false);
        }

        prevFilters.current = selectedFilters;
        prevDate.current = dateString;

        // eslint-disable-next-line
    }, [selectedFilters, selectedDetailsFilters, dateString, detailsDateString]);

    useEffect(() => {
        if (isLoading || !segmentOverviewData?.length) return;
        const labels = segmentOverviewData.map(s => formatSegmentName(s.segment));
        const values = segmentOverviewData.map(s => s.total_cases);
        const canvasId = "segmentOverviewCanvasWidget";
        const existing = ChartJS.getChart(canvasId);
        if (existing) existing.destroy();
        const canvas = document.getElementById(canvasId);
        if (!canvas) return;
        new ChartJS(canvas, {
            type: "doughnut",
            data: {
                labels,
                datasets: [
                    { data: values, backgroundColor: tableColors }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: { enabled: true }
                }
            }
        });
    }, [segmentOverviewData, isLoading]);

    return (
        <Col
            className="h-100 d-flex flex-column"
            onMouseEnter={() => setIsWidgetHovered(true)}
            onMouseLeave={() => setIsWidgetHovered(false)}
            style={{ position: 'relative' }}
        >
            <Row className="d-flex align-items-center">
                <Col className="w-fc pr-0">
                    <div className="d-flex align-items-center px-0">
                        <Title title={widgetTitle} />
                        <Tooltip title="Breakdown of volume and cases by segment." arrow>
                            <div className="pt-3 pb-2">
                                <IoMdInformationCircle
                                    className="text-size-18"
                                    style={{ color: Theme().colors.accent }}
                                />
                            </div>
                        </Tooltip>
                    </div>
                </Col>

                {(widgetOptionsOpen || isWidgetHovered) && (
                    <>
                        <Col className="w-fc px-1 pt-3 pb-2">
                            <Button
                                block
                                className="py-1 button-label replaceWidgetButton"
                                size="sm"
                                color="blueFilter"
                                onClick={() => onClickWidgetDrawerOpen("Replace Widget", true, i)}
                            >
                                <BsPencilSquare color="white" />
                            </Button>
                        </Col>
                        <Col className="w-fc px-1 pt-3 pb-2">
                            <Button
                                block
                                className="py-1 button-label deleteWidgetButton"
                                size="sm"
                                color="redLight"
                                onClick={() => onClickDeleteWidget(w)}
                            >
                                <FaTrash color="white" />
                            </Button>
                        </Col>
                    </>
                )}

                <Col className="w-fc me-3 ms-auto px-1 pt-3 pb-2">
                    <Button
                        block
                        className="py-1 button-label expandWidgetButton"
                        size="sm"
                        outline={true}
                        color={Theme().colors.accentName}
                        onClick={onClickSeeDetails}
                    >
                        <FaClipboardList
                            color={Theme().colors.accentName}
                            style={{ position: 'relative', bottom: '1px' }}
                        />{" "}
                        See Details
                    </Button>
                </Col>
            </Row>
            {segmentOverviewData?.length <= 0 && (
                <Row className="mx-auto flex-grow-1 d-flex justify-content-center align-items-center">
                    {isLoading ? (
                        <Spinner color="beplBlue" />
                    ) : filtersEnabled ? (
                        <EmptySearchResultsMessage />
                    ) : (
                        <EmptyDataMessage />
                    )}
                </Row>
            )}
            {segmentOverviewData?.length > 0 && (
                <Row className="w-100 mb-3 mx-0 flex-grow-1" style={{ minHeight: "315px" }}>
                    <Col
                        className="p-0 d-flex justify-content-center align-items-center"
                        style={{ flex: "0 0 42%", height: "100%" }}
                    >
                        <div style={{ width: "90%", height: "90%" }}>
                            <canvas
                                id="segmentOverviewCanvasWidget"
                                style={{ width: "100%", height: "100%" }}
                            />
                        </div>
                    </Col>
                    <Col
                        className="p-0 text-size-14 d-flex"
                        style={{ flex: "0 0 58%", height: "100%", minHeight: 0 }}
                    >
                        <AutoSizer>
                            {({ width, height }) => (
                                <Table
                                    className="table segmentOverviewTable border-grayLight"
                                    width={width}
                                    height={height}
                                    headerHeight={34.5}
                                    rowHeight={38}
                                    rowCount={segmentOverviewData.length}
                                    rowGetter={({ index }) => segmentOverviewData[index]}
                                >
                                    <Column
                                        label="Segment"
                                        dataKey="segment"
                                        width={width * 0.50}
                                        cellRenderer={({ cellData }) =>
                                            formatSegmentName(cellData)
                                        }
                                    />
                                    <Column
                                        label="Total Cases"
                                        dataKey="total_cases"
                                        width={width * 0.25}
                                        cellRenderer={({ cellData }) =>
                                            numberFormatter.format(cellData)
                                        }
                                    />
                                    <Column
                                        label="% of Sales"
                                        dataKey="sales_percent"
                                        width={width * 0.25}
                                        cellRenderer={({ cellData }) =>
                                            percentFormatter.format(cellData)
                                        }
                                    />
                                </Table>
                            )}
                        </AutoSizer>
                    </Col>
                </Row>
            )}
        </Col>
    );
};

export default SegmentOverviewWidget;